<template>
  <div
    class="price-type"
    :class="isMountedInCheckout ? 'price-type-line-item' : 'price-type-product-page'"
    v-if="isNotFixedPrice"
    :tooltip="priceTypeTooltip"
  >
    <p class="price-type__message" data-test-price-type-message>{{ priceTypeMessage }}</p>
    <button
      :aria-label="i18n._(`En savoir plus sur ce type de prix`)"
      class="adn-icon adn-icon--function-information price-type__icon-information"
      data-test-to-know-more
      @click="showPriceTypeMessageModal"
    ></button>
  </div>
</template>

<script lang="ts">
import { i18n } from '@b2ag/locale'
import { computed, defineComponent, type PropType } from 'vue'
import { isTemporaryPrice, isPreorderPrice } from '../../offer.business'
import type { PriceType } from '../../offer'
import { getPriceTypeMessage, getPriceTypeTooltip } from './helpers'

export default defineComponent({
  name: 'POfferPriceType',
  props: {
    priceType: {
      type: String as PropType<PriceType>,
      required: true,
    },
    isMountedInCheckout: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['openPreTaxPrice'],
  setup(props, { emit }) {
    const isNotFixedPrice = computed((): boolean => {
      return isTemporaryPrice(props.priceType) || isPreorderPrice(props.priceType)
    })
    const priceTypeMessage = computed(() => getPriceTypeMessage(props.priceType))
    const priceTypeTooltip = computed(() => {
      if (props.isMountedInCheckout) return ''
      return getPriceTypeTooltip(props.priceType)
    })

    const showPriceTypeMessageModal = () => {
      emit('openPreTaxPrice')
    }

    return {
      isNotFixedPrice,
      priceTypeMessage,
      priceTypeTooltip,
      showPriceTypeMessageModal,
      i18n,
    }
  },
})
</script>

<style lang="scss" scoped>
.price-type {
  cursor: pointer;
}

.price-type.price-type-product-page {
  position: relative;

  &:hover::before,
  &:hover::after {
    opacity: 1;
  }

  &::before {
    content: attr(tooltip);
    opacity: 0;
    position: absolute;
    top: -100%;
    background-color: var(--color-grey-dark);
    color: var(--color-white);
    padding: var(--spacing-1) var(--spacing-2);
    font-size: var(--font-size-s);
    border-radius: var(--border-radius-1);
    line-height: var(--line-height-m);
    min-width: 30rem;
    transition: opacity 0.8s;
    transform: translateY(-80%);
  }

  &::after {
    content: '';
    opacity: 0;
    top: calc(-100% + 0.4rem);
    border-bottom: 0.6rem solid var(--color-grey-dark);
    border-right: 0.6rem solid var(--color-grey-dark);
    border-top: 0.6rem solid transparent;
    border-left: 0.6rem solid transparent;
    position: absolute;
    left: 25%;
    transform: translate(-50%) rotate(45deg);
    transition: opacity 0.8s;
  }
}

.price-type-line-item {
  display: flex;

  @media screen and (min-width: $breakpoint-md) {
    justify-content: flex-end;
  }
}

.price-type-product-page {
  margin-bottom: 5.5px;
  display: flex;
  justify-content: left;
}

.price-type__message {
  color: var(--color-text-light);
  font-size: 14px;

  @media screen and (min-width: $breakpoint-lg) {
    text-align: right;
  }
}

.price-type__more {
  color: var(--color-main);
  text-decoration: underline;
  font-weight: bold;
  cursor: pointer;
}

.price-type__icon-information {
  color: var(--color-main);
  padding: 0;
  border: none;
  background-color: transparent;
}
</style>
